export type AnalyticsEventName =
  | "view_pricing_redirect"
  | "view_products_page"
  | "view_contact_page"
  | "click_home_try_now"
  | "click_view_products"
  | "click_view_documentation"
  | "click_explore_connectors"
  | "click_api_access_cta"
  | "click_contact_sales"
  | "contact_email_click"
  | "api_key_request"
  | "submit_chat_query"
  | "chat_stream_success"
  | "chat_stream_error"
  | "toggle_plugin_available"
  | "toggle_plugin_unavailable";

type AnalyticsParamValue = string | number | boolean | undefined;

export type AnalyticsEventParams = Record<string, AnalyticsParamValue>;

export interface FunnelClickContext {
  surface: string;
  ctaName: string;
  destination: string;
}

const PROMOTIONAL_PRODUCT_PARAMS = {
  api_identity: "gugudata-ai",
  api_name: "GuGuData.AI",
  category: "mcp_data_integration",
  content_type: "promotional_site",
} as const;

declare global {
  interface Window {
    gtag?: (command: "event", eventName: string, params?: Record<string, string | number | boolean>) => void;
    dataLayer?: unknown[];
  }
}

export function trackEvent(eventName: AnalyticsEventName, params: AnalyticsEventParams = {}) {
  if (typeof window === "undefined") {
    return;
  }

  const cleanParams = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ""));
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, cleanParams as Record<string, string | number | boolean>);
    return;
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push(["event", eventName, cleanParams]);
  }
}

export function trackPromotionalFunnelEvent(eventName: AnalyticsEventName, params: AnalyticsEventParams = {}) {
  trackEvent(eventName, {
    ...PROMOTIONAL_PRODUCT_PARAMS,
    ...params,
  });
}

export function buildApiAccessPath(surface: string) {
  const params = new URLSearchParams({
    intent: "api-access",
    source: surface,
  });
  return `/contact?${params.toString()}`;
}

export function trackApiAccessCta({ surface, ctaName, destination }: FunnelClickContext) {
  trackPromotionalFunnelEvent("click_api_access_cta", {
    surface,
    cta_name: ctaName,
    funnel_stage: "api_access_interest",
    destination,
    result_status: "clicked",
  });
}

export function trackContactEmailClick({ surface, ctaName, destination }: FunnelClickContext) {
  trackPromotionalFunnelEvent("contact_email_click", {
    surface,
    cta_name: ctaName,
    funnel_stage: "contact_intent",
    destination,
    result_status: "initiated",
    request_channel: "email",
  });
}

export function trackApiKeyRequest({ surface, ctaName, destination }: FunnelClickContext) {
  trackPromotionalFunnelEvent("api_key_request", {
    surface,
    cta_name: ctaName,
    funnel_stage: "api_key_request",
    destination,
    result_status: "initiated",
    request_channel: "email",
  });
}
